import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Search, FileText, X, Mic, Filter, Image } from "lucide-react";
import {
  NotesState,
  Recording,
  ProcessingStatusDetails,
} from "../../types/medical";
import axios from "../../config/axios";
import { useNavigate } from "react-router-dom";
import { sseService } from "../../services/sseService";
import Button from "../Button";
import { RetryButton } from "./RetryButton";
import { isRetryEligible } from "../../utils/retryEligibility";
import './animations.css'; // Import animations

interface RecordingHistoryProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedRecordingId: string | null;
  onRecordingSelect: (
    id: string,
    status: string,
    details?: ProcessingStatusDetails
  ) => void;
  transcription: string;
  isSending: boolean;
  recordingTitle: string;
  recordingDate: Date;
  notes?: NotesState;
  onRecordingStatusUpdate?: (recordingId: string, status: string, progress?: number, details?: any) => void;
  onRecordingsCountChange?: (count: number) => void;
  onRetryToast?: (message: { type: "error" | "success"; text: string }) => void;
}

interface PaginationData {
  page: number;
  per_page: number;
  total: number;
  pages: number;
}


const TERMINAL_STATUSES = ["completed", "analyzed", "failed"];

// Format status text for display aligned with RecordingPreparationScreen
const formatStatusText = (status: string): string => {
  if (!status) return "Unknown";

  // Convert to lowercase first for consistent matching
  const statusLower = status.toLowerCase();

  // Map specific statuses to user-friendly text
  switch (statusLower) {
    case "analyzing":
      return "ANALYZING";
    case "transcribing":
      return "TRANSCRIBING";
    case "transcribed":
      return "TRANSCRIBED";
    case "ai processing":
      return "AI PROCESSING";
    case "processing":
      return "PROCESSING";
    case "uploading":
      return "UPLOADING";
    case "uploaded":
      return "UPLOADED";
    case "pending":
      return "PENDING";
    case "completed":
      return "COMPLETED";
    case "analyzed":
      return "ANALYZED";
    case "failed":
      return "Contact Support";
    case "loading":
      return "LOADING";
    case "transcript_ready":
      return "AI ANALYZING";
    default:
      // For everything else, just capitalize the first letter of each word
      return status
        .split(/[\s_]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
  }
};

// StatusAnimation component to show different animations based on recording status
const StatusAnimation = ({ status }: { status: string }) => {
  const lowerStatus = status?.toLowerCase();
  
  // Only mark as 'failed' when the status is explicitly 'failed'
  const isFailedStatus = lowerStatus === 'failed';

  // Loading animation - enhanced skeleton animation
  if (lowerStatus === 'loading') {
    return (
      <div className="relative w-5 h-3 mr-1.5 overflow-hidden">
        {/* Background pulse effect */}
        <div className="absolute inset-0 bg-blue-100 rounded-sm opacity-40"></div>
        
        {/* Animated lines with staggered shimmer effect */}
        <div className="absolute inset-0 flex flex-col justify-around py-0.5">
          <div className="h-[2px] w-full rounded-full bg-gradient-to-r from-blue-300 via-blue-400 to-blue-300 opacity-80 animate-shimmer" 
               style={{ backgroundSize: '200% 100%' }}></div>
          
          <div className="h-[2px] w-4/5 rounded-full bg-gradient-to-r from-blue-300 via-blue-500 to-blue-300 opacity-90 animate-shimmer" 
               style={{ backgroundSize: '200% 100%', animationDelay: '0.15s', animationDuration: '1.8s' }}></div>
          
          <div className="h-[2px] w-3/5 rounded-full bg-gradient-to-r from-blue-300 via-blue-400 to-blue-300 opacity-80 animate-shimmer"
               style={{ backgroundSize: '200% 100%', animationDelay: '0.3s', animationDuration: '2s' }}></div>
        </div>
        
        {/* Subtle "loading" dot indicator */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex">
          <div className="w-0.5 h-0.5 rounded-full bg-blue-500 animate-pulse opacity-80"></div>
        </div>
      </div>
    );
  }
  
  // Analyzing animation - yellow spinning indicator
  if (lowerStatus === 'analyzing') {
    return (
      <div className="w-2.5 h-2.5 rounded-full border-2 border-yellow-500 border-t-transparent animate-spin mr-1.5" />
    );
  }
  
  // AI Processing animation - indigo spinning with sparkle effect
  if (lowerStatus === 'ai processing') {
    return (
      <div className="relative w-3.5 h-2.5 mr-1.5">
        <div className="absolute inset-0 w-2.5 h-2.5 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin"></div>
        <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-purple-400 rounded-full opacity-70 animate-ping"></div>
      </div>
    );
  }
  
  // Processing animation - blue spinning with magic dots
  if (lowerStatus === 'processing') {
    return (
      <div className="relative w-3.5 h-2.5 mr-1.5">
        <div className="absolute inset-0 w-2.5 h-2.5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
        <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-indigo-400 rounded-full opacity-70 animate-ping"></div>
      </div>
    );
  }
  
  // Transcribing animation - "slicing" effect
  if (lowerStatus === 'transcribing' || lowerStatus === 'transcribed') {
    return (
      <div className="relative w-3 h-2.5 mr-1.5">
        <div className="absolute inset-y-0 left-0 w-[3px] h-2.5 bg-blue-500"></div>
        <div className="absolute inset-y-0 left-[5px] w-[2px] h-2.5 bg-blue-400 opacity-80"></div>
        <div className="absolute inset-y-0 left-[9px] w-[2px] h-2.5 bg-blue-300 opacity-60"
             style={{ animation: 'slicingMotion 1s infinite ease-in-out' }}></div>
      </div>
    );
  }
  
  // Upload/Uploading animation (spinning)
  if (lowerStatus === 'upload' || lowerStatus === 'uploading') {
    return (
      <div className="w-2.5 h-2.5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin mr-1.5" />
    );
  }
  
  // Pending animation - gray pulsing
  if (lowerStatus === 'pending') {
    return (
      <div className="w-2.5 h-2.5 rounded-full bg-gray-400 opacity-60 animate-pulse mr-1.5" />
    );
  }
  
  // Uploaded animation (file waiting for processing)
  if (lowerStatus === 'uploaded') {
    return (
      <div className="relative w-3 h-2.5 mr-1.5">
        <div className="absolute inset-0 w-2 h-2.5 bg-blue-500 rounded-sm"></div>
        <div className="absolute inset-0 w-2 h-2.5 bg-white rounded-sm origin-bottom scale-y-0"
             style={{ animation: 'fileWaiting 2s infinite ease-in-out' }}></div>
        <div className="absolute -right-1 top-0 w-1 h-1 bg-blue-300 rounded-full opacity-70"
             style={{ animation: 'pulse 2s infinite ease-in-out' }}></div>
      </div>
    );
  }
  
  // Transcript ready (AI analyzing) - magical effect
  if (lowerStatus === 'transcript_ready') {
    return (
      <div className="relative w-3 h-2.5 mr-1.5">
        <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-indigo-500 opacity-70"></div>
        <div className="absolute inset-0 w-2.5 h-2.5 rounded-full border-2 border-indigo-300 opacity-50"
             style={{ animation: 'magicPulse 2s infinite ease-in-out' }}></div>
        <div className="absolute top-0 right-0 w-1 h-1 bg-purple-400 rounded-full"
             style={{ animation: 'aiProcessingDot 3s infinite ease-in-out' }}></div>
      </div>
    );
  }
  
  // Completed status
  if (lowerStatus === 'completed' || lowerStatus === 'analyzed') {
    return (
      <div className="w-2.5 h-2.5 rounded-full bg-green-500 mr-1.5" />
    );
  }
  
  // Error animation - only for explicitly failed status
  if (isFailedStatus) {
    return (
      <div className="w-3 h-3 relative mr-1.5">
        <div className="absolute inset-0 rounded-full bg-red-500" 
          style={{ animation: 'errorPulse 2s ease-in-out infinite' }}></div>
        
        {/* Mini support icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative transform scale-[0.16]">
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="white" 
              strokeWidth="3" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
          </div>
        </div>
      </div>
    );
  }
  
  // Default state for any other status (including unknown states)
  return (
    <div className="w-2.5 h-2.5 rounded-full bg-gray-300 mr-1.5" />
  );
};



export const RecordingHistory: React.FC<RecordingHistoryProps> = ({
  searchQuery,
  onSearchChange,
  selectedRecordingId,
  onRecordingSelect,
  transcription,
  isSending,
  recordingTitle,
  recordingDate,
  notes,
  onRecordingStatusUpdate,
  onRecordingsCountChange,
  onRetryToast,
}) => {
  console.log("🎬 RecordingHistory rendering", {
    selectedRecordingId,
    isSending,
  });

  const [recordings, setRecordings] = useState<Recording[]>([]);
  const recordingsRef = useRef<Recording[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mediaTypeFilter, setMediaTypeFilter] = useState<'all' | 'audio' | 'image'>('all');

  const [loadingRecordingId, setLoadingRecordingId] = useState<string | null>(null); // Track which recording is loading internally
  const selectedRecordingIdRef = useRef(selectedRecordingId);
  const onRecordingStatusUpdateRef = useRef(onRecordingStatusUpdate);
  const onRecordingSelectRef = useRef(onRecordingSelect);

  // Last time (epoch ms) we saw a progress/WebSocket update for each recording -
  // used to detect "Processing"/"transcribing" stuck with no progress for 30+ min.
  const lastActivityRef = useRef<Map<string, number>>(new Map());
  // Recordings that were successfully retried - suppress the button until they
  // re-enter an eligible state (a fresh status update will clear the entry).
  const [retriedRecordingIds, setRetriedRecordingIds] = useState<Set<string>>(new Set());

  // Update refs when props/state change
  useEffect(() => {
    selectedRecordingIdRef.current = selectedRecordingId;
    onRecordingStatusUpdateRef.current = onRecordingStatusUpdate;
    onRecordingSelectRef.current = onRecordingSelect;
  }, [selectedRecordingId, onRecordingStatusUpdate, onRecordingSelect]);
  useEffect(() => {
    recordingsRef.current = recordings;
  }, [recordings]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  });
  const navigate = useNavigate();
  const [allLoaded, setAllLoaded] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  // Keep track of scroll metrics before loading more
  const scrollMetricsRef = useRef<{
    scrollHeight: number;
    scrollPosition: number;
    clientHeight: number;
    scrollContainerRef: Element | null;
  } | null>(null);
  
  // Use a persistent DOM reference to avoid scroll jumps
  const scrollContainerRef = useRef<Element | null>(null);

  // Optimistically flip a recording into the normal "Processing" UI after a
  // successful retry - resumes the same WebSocket/polling flow a fresh recording uses.
  const handleRetryStarted = useCallback(
    (recordingId: string) => {
      lastActivityRef.current.set(recordingId, Date.now());
      setRetriedRecordingIds((prev) => new Set(prev).add(recordingId));

      setRecordings((prev) =>
        prev.map((rec) =>
          rec.id.toString() === recordingId
            ? {
                ...rec,
                status: "Processing",
                processing_status: {
                  status: "Processing",
                  details: {
                    ...rec.processing_status?.details,
                    is_processing: true,
                    is_uploaded: true,
                    is_processed: false,
                    has_error: false,
                    progress: 5,
                    message: "Retrying processing...",
                  },
                },
              }
            : rec
        )
      );

      if (selectedRecordingIdRef.current === recordingId) {
        onRecordingStatusUpdateRef.current?.(recordingId, "Processing", 5, {
          message: "Retrying processing...",
        });
      }
    },
    []
  );

  const handleRetryError = useCallback(
    (recordingId: string, message: string) => {
      onRetryToast?.({ type: "error", text: message });
    },
    [onRetryToast]
  );

  // Handle recording selection
  const handleRecordingSelect = useCallback(
    async (recording: Recording, transactionId?: string) => {
      try {
        console.log("Fetching recording details:", recording.id, "with transaction_id:", transactionId);
        
        // Track loading state internally, but don't show it in the list
        setLoadingRecordingId(recording.id.toString());
        
        // Show skeleton UI immediately in the content area by selecting the recording with a loading state
        onRecordingSelect(
          recording.id.toString(),
          "loading", // Special status to indicate loading
          {
            is_processing: true,
            is_uploaded: true,
            is_processed: false,
            has_error: false,
            progress: 0,
            message: "Loading recording data...",
            isSkeletonLoading: true, // Special flag for skeleton UI
          }
        );
        
        // Build URL with optional transaction_id parameter
        let url = `/api/scribing/speech/recordings/${recording.id}`;
        if (transactionId) {
          url += `?transaction_id=${transactionId}`;
        }
        
        const response = await axios.get(url);
        const recordingData = response.data;
        console.log("API Response:", recordingData);

        // Clear loading state
        setLoadingRecordingId(null);
        
        onRecordingSelect(
          recording.id.toString(),
          getRecordingStatus(recording),
          recordingData // Pass the entire response data
        );
      } catch (error) {
        console.error("Error fetching recording details:", error);
        // Clear loading state
        setLoadingRecordingId(null);
        
        // Still select the recording but show error state
        onRecordingSelect(recording.id.toString(), "failed", {
          is_processing: false,
          is_uploaded: true,
          is_processed: false,
          has_error: true,
          progress: 0,
          message: "Failed to load recording details",
        });
      }
    },
    [onRecordingSelect]
  );

  // Set up WebSocket listener for real-time updates
  useEffect(() => {
    console.log("Setting up WebSocket listener for recording updates");
    let listenerControl: { success: boolean; disconnect: () => void } | null = null;

    const setupListener = async () => {
      try {
        // Use the sseService to set up WebSocket listener
        listenerControl = await sseService.listenToUserRecordings(
          (recordingId, status, progress, details) => {
            // Get the event type from details
            const eventType = details?.event_type || '';
            
            console.log(`📡 WebSocket update: recording ${recordingId}, status: ${status}, event: ${eventType}, progress: ${progress}%`);

            // Record activity so the stale-processing Retry check (30+ min with no update) works
            lastActivityRef.current.set(recordingId.toString(), Date.now());
            // Any live update means the recording has re-entered active processing -
            // clear a prior "retried" suppression so Retry can show again if it stalls once more.
            setRetriedRecordingIds((prev) => {
              if (!prev.has(recordingId.toString())) return prev;
              const next = new Set(prev);
              next.delete(recordingId.toString());
              return next;
            });

            // Update existing recording in state OR add new recording
            setRecordings((prevRecordings) => {
              const existingRecording = prevRecordings.find((rec) => rec.id === recordingId);
              
              if (!existingRecording) {
                console.log(`📝 New recording ${recordingId} - adding to list`);
                console.log(`📋 WebSocket details:`, { recordingId, status, progress, eventType, details });
                // Create new recording object and add to the beginning of the list
                const newRecording: Recording = {
                  id: recordingId,
                  title: details?.metadata?.patient_name ? `${details.metadata.patient_name} - Visit` : "New Recording",
                  date: details?.timestamp || new Date().toISOString(),
                  last_processed: null,
                  last_transaction_id: null,
                  patient_name: details?.metadata?.patient_name || "Unknown",
                  created_at: details?.timestamp || new Date().toISOString(),
                  status: status,
                  processing_status: {
                    status: status,
                    details: {
                      is_processing: !['completed', 'analyzed'].includes(status.toLowerCase()) && status.toLowerCase() !== "failed",
                      is_uploaded: ['uploaded', 'transcribing', 'transcribed', 'ai processing', 'processing', 'analyzing', 'analyzed', 'completed'].includes(status.toLowerCase()),
                      is_processed: ['completed', 'analyzed'].includes(status.toLowerCase()),
                      has_error: status.toLowerCase() === "failed",
                      progress: progress,
                      message: `${eventType}: ${progress}%`,
                    }
                  }
                };
                return [newRecording, ...prevRecordings];
              }

              // Check if this recording is already in completed state
              const currentStatus = existingRecording?.processing_status?.status || existingRecording?.status || '';
              const currentProgress = existingRecording?.processing_status?.details?.progress || 0;
              
              // Skip update if recording is already in completed state and this isn't a failure
              if (currentStatus.toLowerCase() === "completed" && status.toLowerCase() !== "failed") {
                console.log(`Ignoring status update for recording ${recordingId} - already in completed state`);
                return prevRecordings;
              }

              // Skip update if new progress is lower than current progress (ignore stale/out-of-order events)
              if (progress < currentProgress && status.toLowerCase() !== "failed" && status.toLowerCase() !== "completed") {
                console.log(`Ignoring stale progress update for recording ${recordingId} - current: ${currentProgress}%, new: ${progress}%`);
                return prevRecordings;
              }

              return prevRecordings.map((rec) =>
                rec.id === recordingId
                  ? {
                      ...rec,
                      status: status, // Update main status field
                      processing_status: rec.processing_status 
                        ? {
                            ...rec.processing_status,
                            status: status,
                          } 
                        : { 
                            status: status,
                                                          details: {
                                is_processing: !['completed', 'analyzed'].includes(status.toLowerCase()) && status.toLowerCase() !== "failed",
                                is_uploaded: ['uploaded', 'transcribing', 'transcribed', 'ai processing', 'processing', 'analyzing', 'analyzed', 'completed'].includes(status.toLowerCase()),
                                is_processed: ['completed', 'analyzed'].includes(status.toLowerCase()),
                                has_error: status.toLowerCase() === "failed",
                                progress: progress,
                                message: `${eventType}: ${progress}%`,
                              }
                          },
                    }
                  : rec
              );
            });

            // Handle selected recording updates - use refs to avoid stale closures
            if (selectedRecordingIdRef.current === recordingId.toString()) {
              console.log("Selected recording status changed to:", status, "event:", eventType, "progress:", progress);
              onRecordingStatusUpdateRef.current?.(recordingId.toString(), status, progress, details);
              
              // When recording completes, fetch fresh data and update in-place (no page reload)
              if (status.toLowerCase() === "completed") {
                try {
                  console.log(`Recording ${recordingId} completed - fetching completed data in-place`);

                  // Small delay to let backend finalize
                  setTimeout(async () => {
                    try {
                      const url = `/api/scribing/speech/recordings/${recordingId}`;
                      const response = await axios.get(url);
                      const recordingData = response.data;
                      console.log("Fetched completed recording data in-place:", recordingData);

                      // Only update if this recording is still the selected one
                      if (selectedRecordingIdRef.current === recordingId.toString()) {
                        onRecordingSelectRef.current(
                          recordingId.toString(),
                          "completed",
                          recordingData
                        );
                      }
                    } catch (error) {
                      console.error("Error fetching completed recording data:", error);
                    }
                  }, 2500);
                } catch (error) {
                  console.error("Error handling completed recording:", error);
                }
              } else {
                // For all other statuses, just update the UI without refreshing
                console.log(`Recording ${recordingId} status updated to ${status} - no page refresh needed`);
              }
            }
          }
        );

        if (!listenerControl.success) {
          console.error("Failed to set up WebSocket listener");
        }
      } catch (error) {
        console.error("Error setting up WebSocket listener:", error);
      }
    };

    // Run the setup
    setupListener();

    // Clean up listener
    return () => {
      if (listenerControl) {
        console.log("Disconnecting WebSocket listener");
        listenerControl.disconnect();
      }
    };
  }, []); // Empty dependency array to prevent reconnections

  // Track whether we have non-terminal recordings for polling
  const hasNonTerminalRef = useRef(false);

  useEffect(() => {
    hasNonTerminalRef.current = recordings.some((rec) => {
      const status = (rec.processing_status?.status || rec.status || "").toLowerCase();
      return status && !TERMINAL_STATUSES.includes(status);
    });
  }, [recordings]);

  // Polling fallback: re-fetch recordings every 30s if any are in a non-terminal state
  useEffect(() => {
    const POLL_INTERVAL = 30000; // 30 seconds

    const intervalId = setInterval(async () => {
      if (!hasNonTerminalRef.current) return;

      try {
        const response = await axios.get(
          `/api/scribing/speech/recordings?page=1&per_page=${pagination.per_page}`
        );
        if (response.data && Array.isArray(response.data.items)) {
          const updatedMap = new Map(
            response.data.items.map((item: Recording) => [item.id.toString(), item])
          );

          // Check if the selected recording just reached a terminal status
          // Read old status from ref to avoid setState-during-render warning
          const selectedId = selectedRecordingIdRef.current;
          if (selectedId) {
            const updatedSelected = updatedMap.get(selectedId) as Recording | undefined;
            if (updatedSelected) {
              const newStatus = (updatedSelected.processing_status?.status || updatedSelected.status || "").toLowerCase();
              const oldRec = recordingsRef.current.find((r) => r.id.toString() === selectedId);
              const oldStatus = (oldRec?.processing_status?.status || oldRec?.status || "").toLowerCase();
              if (!TERMINAL_STATUSES.includes(oldStatus) && TERMINAL_STATUSES.includes(newStatus)) {
                // Status just transitioned to terminal - fetch full recording data
                setTimeout(async () => {
                  try {
                    console.log(`Polling: recording ${selectedId} completed - fetching full data`);
                    const fullResponse = await axios.get(`/api/scribing/speech/recordings/${selectedId}`);
                    if (selectedRecordingIdRef.current === selectedId) {
                      onRecordingSelectRef.current(selectedId, newStatus, fullResponse.data);
                    }
                  } catch (error) {
                    console.error("Error fetching completed recording data from polling:", error);
                  }
                }, 1000);
              }
            }
          }

          setRecordings((prev) => {
            return prev.map((rec) => {
              const updated = updatedMap.get(rec.id.toString()) as Recording | undefined;
              return updated || rec;
            });
          });
        }
      } catch (err) {
        console.error("Polling refresh failed:", err);
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [pagination.per_page]);

  // Simple useEffect to maintain scroll position when new items are loaded
  useEffect(() => {
    // Only run when loading more completes
    if (isLoadingMore === false && scrollMetricsRef.current) {
      if (!scrollContainerRef.current) {
        scrollContainerRef.current = document.querySelector('.flex-1.overflow-y-auto');
      }
      
      const { scrollPosition } = scrollMetricsRef.current;
      const scrollContainer = scrollContainerRef.current;
      
      if (scrollContainer) {
        // Simple direct scroll position maintenance - no animations
        scrollContainer.scrollTop = scrollPosition;
      }
      
      // Reset metrics
      scrollMetricsRef.current = null;
    }
  }, [isLoadingMore]);

  // Extract fetchRecordings to a function we can call from multiple places
  const refreshRecordingsList = async (
    page: number,
    perPage: number,
    forceRefresh = false
  ) => {
    console.log(
      `📥 Fetching recordings: page ${page}, perPage ${perPage}${
        forceRefresh ? " (forced)" : ""
      }`
    );

    try {
      // Only set isLoading for first page
      if (page === 1) {
        setIsLoading(true);
      }
      setError(null);
      
      const response = await axios.get(
        `/api/scribing/speech/recordings?page=${page}&per_page=${perPage}`
      );
      console.log("✅ Fetched recordings:", response.data);

      if (response.data && Array.isArray(response.data.items)) {
        // If this is the last page or no items returned, mark as all loaded
        if (page >= response.data.pagination?.pages || response.data.items.length === 0) {
          setAllLoaded(true);
        } else {
          setAllLoaded(false);
        }
        
        // Apply recordings update in a way that prevents UI flickering
        if (page === 1) {
          // For first page, just replace everything
          setRecordings(response.data.items);
        } else {
          // For subsequent pages, append without triggering animations
          setRecordings(prev => {
            // Get existing IDs to avoid duplicates
            const existingIds = new Set(prev.map(r => r.id.toString()));
            
            // Filter out any duplicates from new items
            const newItems = response.data.items.filter(
              item => !existingIds.has(item.id.toString())
            );
            
            return [...prev, ...newItems];
          });
        }
        
        if (response.data.pagination) {
          setPagination(response.data.pagination);
          // Call onRecordingsCountChange with the total count
          onRecordingsCountChange?.(response.data.pagination.total || 0);
        }
      }
    } catch (err) {
      console.error("❌ Error fetching recordings:", err);
      setError("Failed to load recordings. Please try again later.");
    } finally {
      setIsLoading(false);
      // Reset loading more state
      setIsLoadingMore(false);
    }
  };

  // Fetch recordings effect
  useEffect(() => {
    refreshRecordingsList(pagination.page, pagination.per_page);
  }, [pagination.page, pagination.per_page, onRecordingsCountChange]);

  // Select first recording when recordings are loaded
  useEffect(() => {
    if (recordings.length > 0 && !selectedRecordingId) {
      const firstRecording = recordings[0];
      console.log("Selecting first recording:", firstRecording);
      handleRecordingSelect(firstRecording);
    }
  }, [recordings, selectedRecordingId, handleRecordingSelect]);

  // Filter recordings based on search and media type with null checks
  const filteredRecordings = useMemo(() => {
    return recordings.filter((recording) => {
      // Make sure the recording object and title exist
      if (!recording || typeof recording.title !== "string") return false;

      // Filter by search query
      const query =
        typeof searchQuery === "string" ? searchQuery.toLowerCase() : "";
      const matchesSearch = recording.title.toLowerCase().includes(query);

      // Filter by media type
      const matchesMediaType = mediaTypeFilter === 'all' ||
        (mediaTypeFilter === 'image' && recording.media_type === 'image') ||
        (mediaTypeFilter === 'audio' && (recording.media_type === 'audio' || !recording.media_type));

      return matchesSearch && matchesMediaType;
    });
  }, [recordings, searchQuery, mediaTypeFilter]);

  // Add status color mapping aligned with RecordingPreparationScreen
  const getStatusColor = (status: string): string => {
    // Convert to lowercase for consistent matching
    const statusLower = status.toLowerCase();

    switch (statusLower) {
      case "completed":
      case "analyzed":
        return "bg-green-100 text-green-700";
      case "analyzing":
        return "bg-yellow-100 text-yellow-700";
      case "transcribing":
      case "transcribed":
        return "bg-blue-100 text-blue-700";
      case "ai processing":
        return "bg-indigo-100 text-indigo-700";
      case "processing":
      case "ready":
      case "transcript_ready":
      case "loading":
        return "bg-blue-100 text-blue-700";
      case "uploading":
      case "upload":
        return "bg-blue-100 text-blue-700";
      case "uploaded":
        return "bg-blue-100 text-blue-700";
      case "pending":
        return "bg-gray-100 text-gray-700";
      case "failed":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // Helper function to get status regardless of where it's stored
  const getRecordingStatus = (recording: Recording): string => {
    // Check processing_status first (updated by WebSocket), then fallback to main status
    const status = recording.processing_status?.status || recording.status || 'unknown';
    
    return status;
  };

  const handleLoadMore = () => {
    if (pagination.page < pagination.pages && !isLoadingMore) {
      // Use our persistent ref to the scroll container
      if (!scrollContainerRef.current) {
        scrollContainerRef.current = document.querySelector('.flex-1.overflow-y-auto');
      }
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return;
      
      // Store scroll metrics before loading more - using the persistent reference
      scrollMetricsRef.current = {
        scrollHeight: scrollContainer.scrollHeight,
        scrollPosition: scrollContainer.scrollTop,
        clientHeight: scrollContainer.clientHeight,
        scrollContainerRef: scrollContainer
      };
      
      // Set loading state
      setIsLoadingMore(true);
      
      // Update pagination to fetch next page
      setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  return (
    <div className="w-80 sm:w-80 xs:w-72 border-r border-[#E5E5EA] flex flex-col h-full bg-white min-h-0">
      <div className="flex-shrink-0 p-4 border-b border-[#E5E5EA] bg-white">
        <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3">
          All Notes
        </h2>
        <div className="relative">
          <input
            type="text"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-8 py-2 text-sm text-[#1D1D1F] bg-[#F5F5F7] rounded-full placeholder:text-[#86868B] focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:ring-opacity-50"
          />
          <Search className="w-4 h-4 text-[#86868B] absolute left-4 top-1/2 transform -translate-y-1/2" />
          
          {/* Clear button - only show when there's text */}
          {searchQuery && (
            <button 
              onClick={() => onSearchChange("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-200 rounded-full p-1 transition-colors"
              aria-label="Clear search"
            >
              <X className="w-3 h-3 text-gray-600" />
            </button>
          )}
        </div>

        {/* Media Type Filter */}
        <div className="flex gap-1 mt-3">
          <button
            onClick={() => setMediaTypeFilter('all')}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              mediaTypeFilter === 'all'
                ? 'bg-[#0066FF] text-white'
                : 'bg-[#F5F5F7] text-[#86868B] hover:bg-[#E5E5EA]'
            }`}
          >
            <Filter className="w-3 h-3 inline mr-1" />
            All
          </button>
          <button
            onClick={() => setMediaTypeFilter('audio')}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              mediaTypeFilter === 'audio'
                ? 'bg-[#0066FF] text-white'
                : 'bg-[#F5F5F7] text-[#86868B] hover:bg-[#E5E5EA]'
            }`}
          >
            <Mic className="w-3 h-3 inline mr-1" />
            Audio
          </button>
          <button
            onClick={() => setMediaTypeFilter('image')}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              mediaTypeFilter === 'image'
                ? 'bg-[#0066FF] text-white'
                : 'bg-[#F5F5F7] text-[#86868B] hover:bg-[#E5E5EA]'
            }`}
          >
            <Image className="w-3 h-3 inline mr-1" />
            Images
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-white min-h-0">
        {/* Loading State - Show when API data is loading */}
        {(isLoading && pagination.page === 1) ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 rounded-full border-4 border-[#0066FF] border-t-transparent animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">Loading visits...</p>
          </div>
        ) : null}

        {/* Error State */}
        {error && (
          <div className="p-8 text-center">
            <div 
              className="relative w-16 h-16 mx-auto mb-4"
              style={{ animation: 'errorShake 0.82s cubic-bezier(.36,.07,.19,.97) both' }}
            >
              {/* Background circle */}
              <div className="absolute inset-0 bg-red-100 rounded-full"></div>
              
              {/* Pulsing border */}
              <div className="absolute inset-0 rounded-full border-4 border-red-400 opacity-50"
                style={{ animation: 'errorPulse 3s ease-in-out infinite' }}></div>
              
              {/* Support icon - headset/phone */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-8 h-8" style={{ animation: 'errorFloat 2s ease-in-out infinite' }}>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    className="text-red-500"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
              </div>
              
              {/* Error code rotation */}
              <div className="absolute inset-0 origin-center"
                style={{ animation: 'errorRotate 15s linear infinite' }}>
                {[0, 1].map((index) => (
                  <div
                    key={index}
                    className="absolute"
                    style={{
                      top: 'calc(50% - 2px)',
                      left: 'calc(50% - 2px)',
                      transform: `rotate(${index * 180}deg) translateY(-22px)`,
                    }}
                  >
                    <div 
                      className="text-xs font-mono font-bold px-1.5 py-0.5 bg-red-400 text-white rounded"
                      style={{ 
                        animation: `warningFlash 1.5s ease-in-out ${index * 0.5}s infinite`
                      }}
                    >
                      {index === 0 ? 'ERR' : (Date.now().toString().slice(-3))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-sm text-red-500 font-medium mb-2">{error}</p>
            <p className="text-xs text-red-400 mb-4">Please contact customer support with error code: {Date.now().toString().slice(-6)}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="mt-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 font-medium rounded-lg transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 12C2 16.97 6.03 21 11 21C13.39 21 15.68 20.06 17.4 18.4L15.9 16.9C14.63 18.25 12.86 19 11 19C4.76 19 1.64 11.46 6.05 7.05C10.46 2.64 18 5.77 18 12H15L19 16H19.1L23 12H20C20 7.03 15.97 3 11 3C6.03 3 2 7.03 2 12Z" fill="currentColor"/>
                </svg>
                Try Again
              </button>
              <button
                onClick={() => window.open('mailto:support@scribedo.com?subject=Error%20' + Date.now().toString().slice(-6))}
                className="mt-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium rounded-lg transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 12H12.01M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Contact Support
              </button>
            </div>
          </div>
        )}

        {/* Current Recording */}
        {!isLoading && !error && (transcription || isSending) && (
          <div className={`p-4 group border-b border-[#E5E5EA] ${
            selectedRecordingId === "current" ? "bg-[#F2F2F7] border-r-4 border-r-blue-500" : "hover:bg-[#F2F2F7]"
          }`}>
            <div className="flex items-center justify-between">
              <div
                className={`
                  flex-1 cursor-pointer
                  ${
                    selectedRecordingId === "current"
                      ? "text-blue-600"
                      : "text-gray-900"
                  }
                `}
                onClick={() =>
                  onRecordingSelect("current", "analyzing", {
                    is_processing: true,
                    is_uploaded: true,
                    is_processed: false,
                    has_error: false,
                    progress: 0,
                    message: "Analyzing recording...",
                  })
                }
              >
                <h3 className="text-sm font-semibold mb-1.5">{notes?.processed_content?.processing_options?.patient_name || "Unknown"}</h3>
                <p className="text-xs font-medium text-gray-600 mb-2 flex items-center">
                  {recordingTitle}
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-gray-500">
                    {recordingDate.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                    })}
                  </p>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center 
                    ${
                      isSending
                        ? getStatusColor("analyzing")
                        : transcription
                        ? getStatusColor("completed")
                        : getStatusColor("ready")
                    }
                  `}
                  >
                    {isSending && <StatusAnimation status="analyzing" />}
                    {transcription && <StatusAnimation status="completed" />}
                    {!isSending && !transcription && <StatusAnimation status="ready" />}
                    {isSending
                      ? formatStatusText("analyzing")
                      : transcription
                      ? formatStatusText("completed")
                      : formatStatusText("ready")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recordings List */}
        {!isLoading &&
          !error &&
          filteredRecordings.map((recording) => (
            <div
              key={recording.id}
              className={`
              p-4 group border-b border-[#E5E5EA] transition-colors
              ${
                selectedRecordingId === recording.id.toString()
                  ? "bg-[#F2F2F7] border-r-4 border-r-blue-500"
                  : "hover:bg-[#F2F2F7]"
              }
              ${
                getRecordingStatus(recording).toLowerCase() === "failed"
                  ? "cursor-not-allowed opacity-75"
                  : "cursor-pointer"
              }
              ${loadingRecordingId === recording.id.toString() ? "relative" : ""}
            `}
              onClick={() => handleRecordingSelect(recording)}
            >
              {/* Subtle loading indicator that doesn't interfere with the content layout */}
              {loadingRecordingId === recording.id.toString() && (
                <div className="absolute right-2 top-2 flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 opacity-70 animate-subtlePulse"></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 opacity-70 animate-subtlePulse ml-1" 
                       style={{ animationDelay: '0.3s' }}></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 opacity-70 animate-subtlePulse ml-1"
                       style={{ animationDelay: '0.6s' }}></div>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div
                  className={`
                  flex-1
                  ${
                    selectedRecordingId === recording.id.toString()
                      ? "text-[#0A84FF]"
                      : "text-[#1D1D1F]"
                  }
                `}
                >
                  <h3 className="text-sm font-semibold mb-1.5"> {recording.patient_name || "Unknown"}</h3>
                  <p className="text-sm font-medium text-gray-600 mb-2 flex items-center">
                    {recording.media_type === 'image' && (
                      <Image className="w-3.5 h-3.5 text-blue-500 mr-1.5 flex-shrink-0" />
                    )}
                    {(!recording.media_type || recording.media_type === 'audio') && (
                      <Mic className="w-3.5 h-3.5 text-green-500 mr-1.5 flex-shrink-0" />
                    )}
                    <span className="truncate">{recording.title}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-[#86868B]">
                      {new Date(recording.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        year: "numeric",
                      })}
                    </p>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center ${getStatusColor(
                        getRecordingStatus(recording)
                      )}`}
                    >
                      <StatusAnimation status={getRecordingStatus(recording)} />
                      {formatStatusText(getRecordingStatus(recording))}
                    </span>
                    {!retriedRecordingIds.has(recording.id.toString()) &&
                      isRetryEligible({
                        status: getRecordingStatus(recording),
                        recordingDate: recording.date || recording.created_at,
                        lastActivityAt: lastActivityRef.current.get(recording.id.toString()),
                        notes: recording.notes,
                      }) && (
                        <RetryButton
                          recordingId={recording.id.toString()}
                          templateId={recording.template?.id}
                          recordingTitle={recording.title}
                          onRetryStarted={handleRetryStarted}
                          onRetryError={handleRetryError}
                        />
                      )}
                  </div>
                </div>
              </div>
            </div>
          ))}

        {/* Load More Button or End of List Message */}
        {!isLoading && !error && (
          <>
            {/* Show Load More if there are more pages */}
            {pagination.page < pagination.pages && !allLoaded && (
              <div className="p-4 text-center border-t border-gray-100">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className={`text-sm flex items-center justify-center w-full py-2 transition-colors ${
                    isLoadingMore 
                      ? 'text-blue-400 cursor-not-allowed' 
                      : 'text-blue-600 hover:text-blue-700'
                  }`}
                >
                  {isLoadingMore ? (
                    <>
                      <div className="flex items-center mr-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 opacity-80 animate-subtlePulse"></div>
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 opacity-80 animate-subtlePulse ml-1" 
                             style={{ animationDelay: '0.3s' }}></div>
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 opacity-80 animate-subtlePulse ml-1"
                             style={{ animationDelay: '0.6s' }}></div>
                      </div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <span className="mr-1.5">Load More</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Loading placeholder when fetching more recordings - subtle indicator */}
            {isLoadingMore && (
              <div className="py-3 text-center border-t border-gray-100">
                <div className="flex items-center justify-center">
                  <div className="w-1 h-1 rounded-full bg-blue-400 mr-1 animate-subtlePulse"></div>
                  <div className="w-1 h-1 rounded-full bg-blue-400 mr-1 animate-subtlePulse" style={{ animationDelay: '0.15s' }}></div>
                  <div className="w-1 h-1 rounded-full bg-blue-400 mr-1 animate-subtlePulse" style={{ animationDelay: '0.3s' }}></div>
                  <div className="w-1 h-1 rounded-full bg-blue-400 animate-subtlePulse" style={{ animationDelay: '0.45s' }}></div>
                </div>
              </div>
            )}
            
            {/* Show end of list message when all loaded */}
            {(pagination.page >= pagination.pages || allLoaded) && filteredRecordings.length > 0 && (
              <div className="p-4 text-center text-xs text-gray-500 border-t border-gray-100">
                <div className="flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  You've reached the end of the list
                </div>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!isLoading &&
          !error &&
          !transcription &&
          !isSending &&
          filteredRecordings.length === 0 && (
            <div className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <FileText className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500">No Notes yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Click{" "}
                <Button
                  variant="secondary"
                  size="sm"
                  className="!px-2 !py-0.5 !shadow-none inline-flex"
                >
                  Start a visit
                </Button>{" "}
                to get started
              </p>
            </div>
          )}
      </div>

      {/* Record Button */}
      <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-white">
        <Button
          onClick={() => navigate("/visit")}
          variant="primary"
          size="md"
          fullWidth
          className="h-12"
        >
          <Mic className="w-4 h-4" /> Start a visit
        </Button>
      </div>
    </div>
  );
};
